/* nodeDetailLayout.js — THE single source of truth for a node-detail PillCard's
   STRUCTURAL layout flags (Edward 2026-07-10).
   ---------------------------------------------------------------------------------
   A node-detail surface (the skill-tree node click / equip-hotbar click) renders a
   PillCard whose *internal path* is chosen by a handful of structural flags:
   sideDetailsLayout (the movable float body), keystoneSections (the two-section
   keystone split), tooltipOnly (the centered fallback), sideDetails, showCost,
   disableHover, hideType. Which flags are set is a PURE function of the surface kind
   (keystone vs drug) and the trigger (float vs read-only tooltip).

   WHY THIS EXISTS: `SkillTreeNodeToast.finalPillProps` and the admin Combat-Surfaces
   gallery's `NodeCardGallery.buildRender` both need these flags. Before this module
   the gallery HAND-MIRRORED finalPillProps, and when the float consolidation moved
   keystone floats onto `sideDetailsLayout`, the mirror silently drifted — the gallery
   kept passing the pre-consolidation prop shape and rendered keystone node-toasts in
   the wrong (compact) style. Binding both callers to this one function makes that
   class of drift STRUCTURAL-impossible instead of vigilance-dependent.

   Contract: this returns ONLY the surface-derived structural flags. Each caller
   spreads the result and adds its own CONTEXTUAL props (card data, footButtons JSX,
   iconOverride, accent, keystoneBonus, lens state) — those are not derivable from the
   surface kind and legitimately differ between the live component and the gallery.

   If you add a structural flag to a node-detail PillCard, add it HERE (and both
   callers get it). Do not re-derive layout flags at a call site. */

export function nodeDetailLayoutProps({
  isKeystone = false,
  asFloat = false,
  readOnly = false,
} = {}) {
  // The centered/tooltip fallback shows the pure-info tooltip on read-only surfaces;
  // any float renders the card body instead. Identical for keystone and drug.
  const tooltipOnly = asFloat ? false : readOnly;

  if (isKeystone) {
    return {
      showCost: false,
      sideDetails: true,
      keystoneSections: true,
      // The float renders the two-section .ks-split keystone body; the centered
      // fallback keeps the keystone-grid tooltip. THIS is the flag that drifted.
      sideDetailsLayout: asFloat,
      tooltipOnly,
      disableHover: false,
    };
  }

  // Drug gold card. The reconciled float layout is opted into by the desktop
  // node/hotbar click (asFloat). Neural Rewire (Psilocybin) rides this SAME
  // canonical float BODY now (Edward 2026-07-11 — it used to fall back to the old
  // compact card, so the imposed toast style skipped that one node). Only the body
  // layout is shared here; its slotless Activate action is supplied by the toast as
  // a bespoke float footer, since it has no equip/unequip/synergy row.
  const useReconciledFloat = asFloat;
  return {
    showCost: false,
    sideDetails: true,
    sideDetailsLayout: useReconciledFloat,
    tooltipOnly,
    disableHover: false,
    hideType: true,
  };
}
